import { useMask } from '@react-input/mask'
import { DateFormatter } from '@repo/toolkit/date'
import { ClockIcon } from '@repo/ui/icons'
import { InputProps } from '@repo/ui/input'
import { useEffect, useState } from 'react'

import { Time } from '../../../../../apps/backoffice/src/lib/types/utils'
import BaseInput from './base-input'
import { getBaseInputProps } from './input-props'
import InputWrapper from './input-wrapper'

export type HourInputProps = Omit<InputProps, 'onChange' | 'value' | 'type'> & {
  onChange?: (value: Time | null) => void
  value?: Time | null
}

const HourInput: React.FC<HourInputProps> = ({
  value,
  onChange,
  active,
  placeholder = 'hh:mm',
  ...props
}) => {
  const baseInputProps = getBaseInputProps(props)
  const inputRef = useMask({
    mask: 'hh:mm',
    replacement: { h: /\d/, m: /\d/ }
  })

  const [inputValue, setInputValue] = useState<string>(() =>
    value ? DateFormatter.getFormattedTime(value) : ''
  )

  useEffect(() => {
    setInputValue(value ? DateFormatter.getFormattedTime(value) : '')
  }, [value])

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    const [hours = '', minutes = ''] = val.split(':')
    const hoursInt = parseInt(hours)
    const minutesInt = parseInt(minutes)

    if (!isNaN(hoursInt)) {
      if (hoursInt > 23) {
        inputRef.current.value = '23' + (minutes ? `:${minutes}` : '')
        setInputValue(inputRef.current.value)
        return
      }
      if (hours.length === 1 && hoursInt > 2) {
        inputRef.current.value = `0${hoursInt}` + (minutes ? `:${minutes}` : '')
        setInputValue(inputRef.current.value)
        return
      }
    }

    if (!isNaN(minutesInt)) {
      if (minutesInt > 59) {
        inputRef.current.value = `${hours}:59`
        setInputValue(inputRef.current.value)
        return
      }
      if (minutes.length === 1 && minutesInt > 5) {
        inputRef.current.value = `${hours}:0${minutesInt}`
        setInputValue(inputRef.current.value)
        return
      }
    }
    setInputValue(val)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (val && val.length === 5 && /^[0-2]\d:[0-5]\d$/.test(val)) {
      const formattedTime = DateFormatter.formatTime(val)
      setInputValue(DateFormatter.getFormattedTime(formattedTime))
      onChange?.(formattedTime)
    } else {
      setInputValue('')
      onChange?.(null)
    }
    props.onBlur?.(e)
  }

  return (
    <InputWrapper active={active || !!inputValue} icon={ClockIcon} {...props}>
      {(_, setIsActive) => (
        <BaseInput
          {...baseInputProps}
          ref={inputRef}
          type="text"
          autoComplete="off"
          inputMode="numeric"
          pattern="[0-9]{2}:[0-9]{2}"
          value={inputValue}
          aria-describedby="time"
          aria-required={props.required}
          aria-invalid={!!props.errorMessage}
          aria-label={props.label}
          setIsActive={setIsActive}
          placeholder={placeholder}
          onInput={handleInput}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      )}
    </InputWrapper>
  )
}

export default HourInput
